// Minimal OBJ parser to support v/vt/vn/f, usemtl groups, triangulation
// Produces per-material groups with de-duplicated interleaved vertices

export type ObjGroup = {
    name: string;
    positions: number[];
    normals: number[];
    uvs: number[];
    indices: number[];
};

export type ObjParseResult = {
    groups: ObjGroup[];
};

// Resolve potentially negative OBJ index (1-based, negative counts from end)
const resolveIndex = (idx: number, count: number) => {
    const resolved = idx > 0 ? idx - 1 : count + idx;
    // Validate bounds to prevent out-of-range access
    if (resolved < 0 || resolved >= count) {
        console.warn(`OBJ index ${idx} out of bounds (count: ${count}), clamping to valid range`);
        return Math.max(0, Math.min(count - 1, resolved));
    }
    return resolved;
};

// Helper to convert a face token to a vertex index in group buffers
function tokenToIndex(
    token: string,
    v: number[][],
    vt: number[][],
    vn: number[][],
    vm: Map<string, number>,
    g: ObjGroup
): number {
    const [viStr, vtiStr, vniStr] = token.split('/');
    const vi = resolveIndex(parseInt(viStr, 10), v.length);
    const vti = vtiStr ? resolveIndex(parseInt(vtiStr, 10), vt.length) : -1;
    const vni = vniStr ? resolveIndex(parseInt(vniStr, 10), vn.length) : -1;
    const key = `${vi}/${vti}/${vni}`;

    let newIdx = vm.get(key);
    if (newIdx === undefined) {
        const pos = v[vi] || [0, 0, 0];
        g.positions.push(pos[0], pos[1], pos[2]);

        if (vni >= 0 && vn[vni]) {
            const n = vn[vni];
            g.normals.push(n[0], n[1], n[2]);
        } else {
            g.normals.push(0, 0, 0);
        }

        if (vti >= 0 && vt[vti]) {
            const uv = vt[vti];
            g.uvs.push(uv[0], uv[1]);
        } else {
            g.uvs.push(0, 0);
        }

        newIdx = g.positions.length / 3 - 1;
        vm.set(key, newIdx);
    }
    return newIdx;
}

export function parseObj(text: string): ObjParseResult {
    const v: number[][] = [];
    const vt: number[][] = [];
    const vn: number[][] = [];

    const groupsMap = new Map<string, ObjGroup>();
    const getGroup = (name: string) => {
        let g = groupsMap.get(name);
        if (!g) {
            g = { name, positions: [], normals: [], uvs: [], indices: [] };
            groupsMap.set(name, g);
        }
        return g;
    };

    let currentGroup = getGroup('default');

    // For each group, map unique vertex (vi/vti/vni) to new index
    const vertMaps = new Map<string, Map<string, number>>();
    const getVertMap = (name: string) => {
        let m = vertMaps.get(name);
        if (!m) {
            m = new Map<string, number>();
            vertMaps.set(name, m);
        }
        return m;
    };

    const lines = text.split(/\r?\n/);
    for (const raw of lines) {
        const line = raw.trim();
        if (!line || line.startsWith('#')) continue;
        const parts = line.split(/\s+/);
        const tag = parts[0];

        if (tag === 'v' && parts.length >= 4) {
            v.push([parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3])]);
        } else if (tag === 'vt' && parts.length >= 3) {
            vt.push([parseFloat(parts[1]), parseFloat(parts[2])]);
        } else if (tag === 'vn' && parts.length >= 4) {
            vn.push([parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3])]);
        } else if (tag === 'usemtl' && parts.length >= 2) {
            const name = parts.slice(1).join(' ');
            currentGroup = getGroup(name);
        } else if (tag === 'f' && parts.length >= 4) {
            // Triangulate fan: f a b c [d ...] -> (a,b,c), (a,c,d), ...
            const faceVerts = parts.slice(1);
            const g = currentGroup;
            const vm = getVertMap(g.name);

            for (let i = 1; i < faceVerts.length - 1; i++) {
                const a = tokenToIndex(faceVerts[0], v, vt, vn, vm, g);
                const b = tokenToIndex(faceVerts[i], v, vt, vn, vm, g);
                const c = tokenToIndex(faceVerts[i + 1], v, vt, vn, vm, g);
                currentGroup.indices.push(a, b, c);
            }
        }
    }

    // Generate normals where missing (per group, per face)
    groupsMap.forEach((g) => {
        const normals = new Array(g.positions.length).fill(0);
        const p = g.positions;
        const idx = g.indices;
        for (let i = 0; i < idx.length; i += 3) {
            const i0 = idx[i] * 3;
            const i1 = idx[i + 1] * 3;
            const i2 = idx[i + 2] * 3;
            const ax = p[i1] - p[i0], ay = p[i1 + 1] - p[i0 + 1], az = p[i1 + 2] - p[i0 + 2];
            const bx = p[i2] - p[i0], by = p[i2 + 1] - p[i0 + 1], bz = p[i2 + 2] - p[i0 + 2];
            // cross = a x b
            const nx = ay * bz - az * by;
            const ny = az * bx - ax * bz;
            const nz = ax * by - ay * bx;
            normals[i0] += nx; normals[i0 + 1] += ny; normals[i0 + 2] += nz;
            normals[i1] += nx; normals[i1 + 1] += ny; normals[i1 + 2] += nz;
            normals[i2] += nx; normals[i2 + 1] += ny; normals[i2 + 2] += nz;
        }
        // normalize
        for (let i = 0; i < normals.length; i += 3) {
            const nx = normals[i], ny = normals[i + 1], nz = normals[i + 2];
            const len = Math.hypot(nx, ny, nz) || 1;
            g.normals[i] = nx / len;
            g.normals[i + 1] = ny / len;
            g.normals[i + 2] = nz / len;
        }
    });

    return { groups: Array.from(groupsMap.values()) };
}
